const CACHE_KEYS = {
  WORKSPACE_DATA: 'workspace_data_cache',
  WORKSPACES_LIST: 'workspaces_list_cache',
  LAST_FETCH_TIME: 'last_fetch_time_cache'
};

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

class CacheManager {
  static set(key, data, timestamp = Date.now()) {
    try {
      const cacheItem = {
        data,
        timestamp,
        expires: timestamp + CACHE_DURATION
      };
      localStorage.setItem(key, JSON.stringify(cacheItem));
    } catch (error) {
    }
  }

  static get(key) {
    try {
      const cached = localStorage.getItem(key);
      if (!cached) return null;

      const cacheItem = JSON.parse(cached);
      const now = Date.now();

      // Return data regardless of expiry for optimistic loading
      return {
        data: cacheItem.data,
        isStale: now > cacheItem.expires,
        timestamp: cacheItem.timestamp,
        age: now - cacheItem.timestamp
      };
    } catch (error) {
      return null;
    }
  }

  static isValid(key) {
    const cached = this.get(key);
    return cached && !cached.isStale;
  }

  static clear(key) {
    try {
      localStorage.removeItem(key);
    } catch (error) {
    }
  }

  static clearAll() {
    Object.values(CACHE_KEYS).forEach(key => {
      this.clear(key);
    });
  }
}

// Workspace-specific cache utilities
export const WorkspaceCache = {
  setWorkspaceData(workspaceId, data) {
    const key = `${CACHE_KEYS.WORKSPACE_DATA}_${workspaceId}`;
    CacheManager.set(key, data);
  },

  getWorkspaceData(workspaceId) {
    const key = `${CACHE_KEYS.WORKSPACE_DATA}_${workspaceId}`;
    return CacheManager.get(key);
  },

  setWorkspacesList(data) {
    CacheManager.set(CACHE_KEYS.WORKSPACES_LIST, data);
  },

  getWorkspacesList() {
    return CacheManager.get(CACHE_KEYS.WORKSPACES_LIST);
  },

  clearWorkspaceData(workspaceId) {
    const key = `${CACHE_KEYS.WORKSPACE_DATA}_${workspaceId}`;
    CacheManager.clear(key);
  },

  clearAll() {
    CacheManager.clearAll();
  },

  // Helper to get current workspace from URL
  getCurrentWorkspaceIdFromUrl() {
    return localStorage.getItem('current_workspace_id');
  },

  // Helper to extract active agent from URL
  getActiveAgentFromUrl() {
    const path = window.location.pathname;
    const match = path.match(/\/agent\/([^/]+)/);
    return match ? match[1] : null;
  },

  // Get cached data for immediate rendering
  getOptimisticData() {
    const currentWorkspaceId = this.getCurrentWorkspaceIdFromUrl();
    if (!currentWorkspaceId) return null;

    const workspaceData = this.getWorkspaceData(currentWorkspaceId);
    const workspacesList = this.getWorkspacesList();

    return {
      currentWorkspaceId,
      workspaceData: workspaceData?.data || null,
      workspacesList: workspacesList?.data || [],
      isWorkspaceDataStale: workspaceData?.isStale || true,
      isWorkspacesListStale: workspacesList?.isStale || true,
      activeAgentId: this.getActiveAgentFromUrl()
    };
  }
};

export default CacheManager;